import { useEffect, useState, useSyncExternalStore } from "react";
import { useCan } from "@/lib/auth";
import type { VoiceEngines } from "@/lib/types";
import { loadedEngines, loadEngines } from "./engines";
import { browserRecognitionSupported, captureSupported } from "./stt";

/**
 * The dictation preference, per device (localStorage), shared by every chat through one
 * tiny store, so flipping it anywhere flips it everywhere.
 */
export const AUTOSEND_KEY = "automation.voice.autoSend";

/**
 * Keys left by read-aloud, removed in Oct 2026: "Speak replies" and the playback debug
 * panel. Dropped once per page so a device that had them carries nothing stale.
 */
export const REMOVED_KEYS = ["automation.voice.speak", "automation.voiceDebug"] as const;

export function dropRemovedVoiceKeys(storage: Pick<Storage, "removeItem"> | undefined): void {
  for (const key of REMOVED_KEYS) {
    try {
      storage?.removeItem(key);
    } catch {
      // Private mode or storage disabled: nothing to drop.
    }
  }
}

type Key = typeof AUTOSEND_KEY;
const listeners = new Set<() => void>();
const memory = new Map<Key, boolean>();

const read = (key: Key): boolean => {
  const held = memory.get(key);
  if (held !== undefined) return held;
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

export function setVoicePref(key: Key, value: boolean): void {
  memory.set(key, value);
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // Private mode: the toggle still holds for this page.
  }
  listeners.forEach((listener) => listener());
}

if (typeof window !== "undefined") {
  dropRemovedVoiceKeys((() => {
    try {
      return window.localStorage;
    } catch {
      return undefined;
    }
  })());
  // Another tab flipped it.
  window.addEventListener("storage", (event) => {
    if (event.key === AUTOSEND_KEY) {
      memory.delete(event.key);
      listeners.forEach((listener) => listener());
    }
  });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

/** Auto-send: a dictation goes straight out when the speaker stops, instead of into the box. */
export function useAutoSend(): [boolean, (value: boolean) => void] {
  const value = useSyncExternalStore(subscribe, () => read(AUTOSEND_KEY));
  return [value, (next) => setVoicePref(AUTOSEND_KEY, next)];
}

/** Whether this device can dictate: the server's recogniser with the browser's as fallback. */
export interface VoiceSupport {
  /**
   * The voice UI is shown at all: an admin, once `GET /api/voice` has been asked. A failed
   * call (voice off on the server: 404/503) still leaves the browser's own recogniser.
   */
  enabled: boolean;
  engines: VoiceEngines;
  /** Something can turn speech into text here (the server, or the browser's recogniser). */
  canListen: boolean;
}

const NONE: VoiceEngines = { stt: false };

export function useVoiceSupport(): VoiceSupport {
  const admin = useCan("admin");
  const [engines, setEngines] = useState(loadedEngines);
  useEffect(() => {
    if (!admin || engines !== undefined) return;
    let live = true;
    void loadEngines().then((result) => live && setEngines(result));
    return () => {
      live = false;
    };
  }, [admin, engines]);
  const enabled = admin && engines !== undefined;
  const value = engines ?? NONE;
  const listen = enabled && ((value.stt && captureSupported()) || browserRecognitionSupported());
  return { enabled, engines: value, canListen: listen };
}
